/**
 * ON THE CALENDAR — which of the platform's calendar events this piece is linked to.
 *
 * ★★ THE CHOICE THE OWNER RULED AND NO SCREEN OFFERED. "when author publihes an article he could
 * choose an event in calendar - so we just save this event" (decision-log row 105 item 7, quoted on
 * `setArticleCalendarEvents` in authors-api.yaml). Until wave 20 neither that operation nor
 * `listArticleCalendarEvents` had a caller under web/authors (header-sweep area 7, F10): the cabinet
 * calendar showed the events and nothing could attach one. `docs/ui/04-pages.md` draws no place for
 * it, so it sits with the editor's other article settings - in the Setup tab since design 15.
 *
 * ★ THE LINKS ARE NOT PART OF THE DRAFT. They are their own resource, replaced WHOLE by one PUT the
 * moment the author links or removes one - not carried by the autosave, not sealed with a version.
 * So every change sends the complete set, built from ids and relations only (the write schema is
 * strict: a link echoed with its read-only `title` is a 400), and the pane shows the set the
 * platform ANSWERED, never the one it asked for.
 *
 * ★ THE EVENTS TO CHOOSE FROM ARE THE CALENDAR'S OWN READ, `getAuthorCalendar`, one UTC month at a
 * time - not `getPublishDateSuggestions`, which is twenty dates ahead of now, and only events that
 * carry an editorial priority (`calendar-cabinet.repository.ts`, `suggestions`). This app keeps no
 * clock, so the month opens on the article's go-live wish, else on the next suggested date, else on
 * the server's own month (design 24, T8: ../../calendar/platform-month.ts); the arrows move it.
 *
 * ★ TWO INTENTS, AND THEY ARE THE CONTRACT'S: `timed_to` (it goes out with the event) and `about` (it
 * is written about it), the rows of `calendar_link_relation` that `ArticleCalendarLink.relation`
 * names. No operation serves that lookup to an author; a link carrying any other code is shown by it.
 *
 * ⚠ A link the article already holds stays, and is still listed, after its event is retired; only a
 * NEW link to an event retired in the meantime is refused 404, "names no live event"
 * (article-calendar-links.service.ts). The pane says so and the author removes that event.
 *
 * READ-ONLY WHERE THE EDITOR IS. While the author cannot write the piece the links are shown and no
 * change is offered, as on every other pane - although the platform gates these two operations on
 * ownership alone, not on the article's status.
 *
 * ★ TOPICS (design 30): beside the dated events, an article may be about a group in general -
 * "Mercury Retrograde", any time - at most five, picked from `listCalendarGroupsForAuthors` grouped
 * by kind. A topic change sends the current event links with the new `groups`; an event change
 * sends no `groups`, which leaves the topics as they are. Names come from the groups list, read in
 * the cabinet's language, else from the links answer.
 */
import { ActionIcon, Alert, Button, Group, NativeSelect, Stack, Text, Title } from '@mantine/core';
import { useCallback, useState } from 'react';
import { useEditorApi } from '../../../features/cabinet/api/editor-api';
import { describeProblem, hasErrorCode, isApiProblem } from '../../../features/cabinet/api/problem';
import { formatDay } from '../../../features/cabinet/format';
import { useAction, useAsync } from '../../../features/cabinet/state';
import { useCalendarApi } from '../../../features/calendar/api';
import { usePlatformMonth } from '../../calendar/platform-month';
import {
  earliestSuggestedMonth,
  monthLabel,
  monthOfInstant,
  monthWindow,
  shiftMonth,
} from '../../../features/calendar/domain/month';
import type {
  ArticleCalendarLinks,
  ArticleCalendarLinksWrite,
  PublishDateSuggestion,
} from '../../../features/cabinet/api/editor-api';
import type { ErrorCode } from '../../../features/cabinet/api/generated';
import type { AuthorCalendarEvent, CalendarGroup } from '../../../features/calendar/api';
import type { MonthKey } from '../../../features/calendar/domain/month';
import { useTranslation } from '../../../i18n';
import type { ArticlesKey, ArticlesT } from '../articles-i18n';

/** The two intents the contract names, and the catalogue's words this pane uses for them. */
export const LINK_RELATIONS = [
  { value: 'timed_to', label: 'editor.calendar.relations.timedTo' },
  { value: 'about', label: 'editor.calendar.relations.about' },
] as const satisfies readonly { readonly value: string; readonly label: ArticlesKey }[];

/** `ArticleCalendarLinksWrite.links` `maxItems: 10`. */
const MAX_LINKS = 10;

/** `ArticleCalendarLinksWrite.groups` `maxItems: 5` (design 30). */
const MAX_TOPICS = 5;

/** The group kinds `CalendarGroupList` names, in the picker's order, and the words for each. */
export const GROUP_KINDS = [
  { value: 'lunar_phase', label: 'editor.calendar.kinds.lunarPhase' },
  { value: 'eclipse', label: 'editor.calendar.kinds.eclipse' },
  { value: 'solar_season', label: 'editor.calendar.kinds.solarSeason' },
  { value: 'zodiac_season', label: 'editor.calendar.kinds.zodiacSeason' },
  { value: 'retrograde', label: 'editor.calendar.kinds.retrograde' },
  { value: 'ingress', label: 'editor.calendar.kinds.ingress' },
  { value: 'holiday', label: 'editor.calendar.kinds.holiday' },
] as const satisfies readonly { readonly value: string; readonly label: ArticlesKey }[];

/**
 * The picker's options: one option group per kind, in `GROUP_KINDS`' order and the catalogue's
 * order inside it; a kind this build does not know goes last, under "Other".
 */
export function topicOptions(
  groups: readonly CalendarGroup[],
  t: ArticlesT,
): { readonly group: string; readonly items: { value: string; label: string }[] }[] {
  const known: readonly string[] = GROUP_KINDS.map((kind) => kind.value);
  const sections = [
    ...GROUP_KINDS.map((kind) => ({
      group: t(kind.label),
      items: groups.filter((group) => group.kind === kind.value),
    })),
    {
      group: t('editor.calendar.kinds.other'),
      items: groups.filter((group) => !known.includes(group.kind)),
    },
  ];
  return sections
    .filter((section) => section.items.length > 0)
    .map((section) => ({
      group: section.group,
      items: section.items.map((group) => ({ value: group.code, label: group.name })),
    }));
}

const NOT_ARTICLE_OWNER: ErrorCode = 'NOT_ARTICLE_OWNER';

/** One link as the write takes it. */
interface LinkPair {
  readonly calendarEventId: string;
  readonly relation: string;
}

export function relationLabel(code: string, t: ArticlesT): string {
  const relation = LINK_RELATIONS.find((each) => each.value === code);
  return relation === undefined ? code : t(relation.label);
}

/**
 * What a refused read or write of the links says. The owner refusal is said here because
 * `describeProblem`'s sentence for that code is about a COMMENT. A WRITE's 404 is, in practice, the
 * retired event in the set (the article itself was just read); a READ's 404 is the article being
 * gone, which `describeProblem` already says. Everything else - `AUTHOR_DISABLED`,
 * `AUTHOR_CAPABILITY_REQUIRED`, a 400 naming what was malformed, no network - is its sentence too.
 */
export function calendarLinksRefusal(
  error: unknown,
  writing: boolean,
  t: ArticlesT,
  topics = false,
): string {
  if (hasErrorCode(error, NOT_ARTICLE_OWNER)) {
    return t('editor.calendar.notYours');
  }
  if (writing && isApiProblem(error) && error.status === 404) {
    // A topic write sends the held event links unchanged, so its 404 is the topic.
    return t(topics ? 'editor.calendar.retiredTopic' : 'editor.calendar.retiredEvent');
  }
  return describeProblem(error);
}

export interface CalendarLinksPanelProps {
  readonly articleId: string;
  readonly disabled: boolean;
  /** The piece's go-live wish, if it has one: the month the picker opens on. */
  readonly plannedGoLiveAt: string | null;
  /** `getPublishDateSuggestions`' items, or undefined while they are still being read. */
  readonly publishDates: readonly PublishDateSuggestion[] | undefined;
}

export function CalendarLinksPanel({
  articleId,
  disabled,
  plannedGoLiveAt,
  publishDates,
}: CalendarLinksPanelProps) {
  const { t, i18n } = useTranslation('articles');
  const api = useEditorApi();
  const calendar = useCalendarApi();

  const read = useAsync(
    useCallback(
      (signal: AbortSignal) => api.listArticleCalendarEvents(articleId, signal),
      [api, articleId],
    ),
    `calendar-links:${articleId}`,
  );
  // The set the last write answered. Once there is one it is the truth, not the opening read.
  const [written, setWritten] = useState<ArticleCalendarLinks | undefined>(undefined);
  const links = (written ?? read.data)?.links;
  const topics = (written ?? read.data)?.groups;

  // The topics to pick from, keyed on the language their names are read in.
  const groupsRead = useAsync(
    useCallback((signal: AbortSignal) => calendar.listCalendarGroups(signal), [calendar]),
    `calendar-groups:${i18n.language}`,
  );
  const groups = groupsRead.data?.items;
  const groupName = (code: string | null | undefined, fallback: string | null | undefined) =>
    groups?.find((group) => group.code === code)?.name ?? fallback ?? undefined;

  const known =
    (plannedGoLiveAt === null ? undefined : monthOfInstant(plannedGoLiveAt)) ??
    earliestSuggestedMonth(publishDates);
  // Neither a wish nor a suggestion: the server's month, as the Calendar page opens (T8).
  const platform = usePlatformMonth(known, publishDates === undefined ? 'loading' : 'ready');
  const [stepped, setStepped] = useState<MonthKey | undefined>(undefined);
  const month = stepped ?? known ?? platform.month;

  // The month travels WITH its answer: `useAsync` keeps the last good data through a new read, and
  // a list of August's events under a September heading would offer the wrong ones.
  const monthRead = useAsync(
    useCallback(
      async (signal: AbortSignal) =>
        month === undefined
          ? undefined
          : { month, answer: await calendar.getAuthorCalendar(monthWindow(month), signal) },
      [calendar, month],
    ),
    `editor-calendar:${month ?? 'none'}`,
  );
  const monthEvents: readonly AuthorCalendarEvent[] | undefined =
    monthRead.data !== undefined && monthRead.data.month === month
      ? monthRead.data.answer.events
      : undefined;

  const [eventId, setEventId] = useState('');
  const [relation, setRelation] = useState<string>(LINK_RELATIONS[0].value);
  const chosen = monthEvents?.find((event) => event.calendarEventId === eventId);

  const save = useAction(async (body: ArticleCalendarLinksWrite) =>
    api.setArticleCalendarEvents(articleId, body),
  );
  // Which kind of write the last one was, so a refusal names the right thing.
  const [topicWrite, setTopicWrite] = useState(false);

  // Ids and relations ONLY - see the header: the write refuses the read-only members.
  const pairsOf = (pairs: readonly LinkPair[]) =>
    pairs.map((link) => ({ calendarEventId: link.calendarEventId, relation: link.relation }));

  const send = (body: ArticleCalendarLinksWrite, topicsChanged: boolean, after?: () => void) => {
    setTopicWrite(topicsChanged);
    void (async () => {
      const answer = await save.run(body);
      if (answer !== undefined) {
        setWritten(answer);
        after?.();
      }
    })();
  };

  // An event change sends no `groups`, so the topics stay as they are.
  const commit = (next: readonly LinkPair[], after?: () => void) => {
    send({ links: pairsOf(next) }, false, after);
  };

  const current: readonly LinkPair[] = links ?? [];
  const currentTopics: readonly string[] = (topics ?? []).map((topic) => topic.groupCode);

  // A topic change sends the event links as they are, and the new topic set.
  const commitTopics = (next: readonly string[], after?: () => void) => {
    send(
      { links: pairsOf(current), groups: next.map((groupCode) => ({ groupCode })) },
      true,
      after,
    );
  };

  const [topicCode, setTopicCode] = useState('');
  const pickedTopic = groups?.find((group) => group.code === topicCode);
  const topicAlready = pickedTopic !== undefined && currentTopics.includes(pickedTopic.code);
  const topicsFull = currentTopics.length >= MAX_TOPICS;
  const canAddTopic =
    !disabled && topics !== undefined && pickedTopic !== undefined && !topicAlready && !topicsFull;
  const alreadyLinked =
    chosen !== undefined &&
    current.some(
      (link) => link.calendarEventId === chosen.calendarEventId && link.relation === relation,
    );
  const full = current.length >= MAX_LINKS;
  const canLink =
    !disabled && links !== undefined && chosen !== undefined && !alreadyLinked && !full;

  return (
    <Stack component="section" gap="md" aria-labelledby="editor-calendar-title">
      <Stack gap={4}>
        <Title id="editor-calendar-title" order={2} size="h5">
          {t('editor.calendar.title')}
        </Title>
        <Text size="xs" c="dimmed">
          {disabled ? t('editor.calendar.introReadOnly') : t('editor.calendar.intro')}
        </Text>
      </Stack>

      {links === undefined ? (
        read.status === 'error' ? (
          <Stack gap={6} align="flex-start">
            <Text size="sm">{calendarLinksRefusal(read.error, false, t)}</Text>
            <Button size="compact-xs" variant="default" onClick={read.refetch}>
              {t('common.tryAgain')}
            </Button>
          </Stack>
        ) : (
          <Text size="sm" c="dimmed">
            {t('editor.calendar.readingLinks')}
          </Text>
        )
      ) : links.length === 0 ? (
        <Text size="sm" c="dimmed">
          {t('editor.calendar.noLinks')}
        </Text>
      ) : (
        <ul
          style={{ listStyle: 'none', margin: 0, padding: 0 }}
          aria-label={t('editor.calendar.linkedLabel')}
        >
          {links.map((link) => {
            const title = link.title ?? t('editor.calendar.untitledEvent'); // never its id (design 15)
            const how = relationLabel(link.relation, t);
            const group = groupName(link.groupCode, link.groupName);
            return (
              <li key={`${link.calendarEventId} ${link.relation}`}>
                <Group justify="space-between" gap="xs" wrap="nowrap" py={4}>
                  <Stack gap={0}>
                    <Text size="sm">{title}</Text>
                    <Text size="xs" c="dimmed">
                      {how}
                      {link.startsAt === null || link.startsAt === undefined
                        ? ''
                        : ` · ${formatDay(link.startsAt.slice(0, 10))}`}
                      {group === undefined ? '' : ` · ${group}`}
                    </Text>
                  </Stack>
                  {disabled ? null : (
                    <Button
                      size="compact-xs"
                      variant="subtle"
                      disabled={save.isRunning}
                      aria-label={t('editor.calendar.removeLink', { title, how })}
                      onClick={() => {
                        commit(current.filter((pair) => pair !== link));
                      }}
                    >
                      {t('editor.calendar.remove')}
                    </Button>
                  )}
                </Group>
              </li>
            );
          })}
        </ul>
      )}

      {disabled ? null : month === undefined ? (
        <Text size="xs" c="dimmed">
          {/* No month only while the server's month is read, or when that read failed (T8). */}
          {publishDates === undefined || platform.pending
            ? t('editor.calendar.readingCalendar')
            : t('editor.calendar.calendarFailed')}
        </Text>
      ) : (
        <Stack gap="xs">
          <Group gap="xs" wrap="nowrap" align="center">
            <ActionIcon
              variant="default"
              aria-label={t('editor.calendar.showMonth', {
                month: monthLabel(shiftMonth(month, -1)),
              })}
              onClick={() => {
                setStepped(shiftMonth(month, -1));
              }}
            >
              <span aria-hidden="true">&#8592;</span>
            </ActionIcon>
            <Text size="sm" fw={700}>
              {monthLabel(month)}
            </Text>
            <ActionIcon
              variant="default"
              aria-label={t('editor.calendar.showMonth', {
                month: monthLabel(shiftMonth(month, 1)),
              })}
              onClick={() => {
                setStepped(shiftMonth(month, 1));
              }}
            >
              <span aria-hidden="true">&#8594;</span>
            </ActionIcon>
          </Group>

          {monthRead.status === 'error' && monthEvents === undefined ? (
            <Stack gap={6} align="flex-start">
              <Text size="sm">
                {t('editor.calendar.monthFailed', {
                  month: monthLabel(month),
                  reason: describeProblem(monthRead.error),
                })}
              </Text>
              <Button size="compact-xs" variant="default" onClick={monthRead.refetch}>
                {t('common.tryAgain')}
              </Button>
            </Stack>
          ) : (
            <NativeSelect
              label={t('editor.calendar.event')}
              disabled={monthEvents === undefined || monthEvents.length === 0}
              value={chosen === undefined ? '' : chosen.calendarEventId}
              data={[
                {
                  value: '',
                  label:
                    monthEvents === undefined
                      ? t('editor.calendar.readingCalendar')
                      : monthEvents.length === 0
                        ? t('editor.calendar.noEvents')
                        : t('editor.calendar.chooseEvent'),
                  disabled: true,
                },
                ...(monthEvents ?? []).map((event) => ({
                  value: event.calendarEventId,
                  label: `${event.title} · ${formatDay(event.startsAt.slice(0, 10))}`,
                })),
              ]}
              onChange={(event) => {
                setEventId(event.currentTarget.value);
              }}
            />
          )}

          <NativeSelect
            label={t('editor.calendar.relation')}
            value={relation}
            data={LINK_RELATIONS.map((option) => ({ value: option.value, label: t(option.label) }))}
            onChange={(event) => {
              setRelation(event.currentTarget.value);
            }}
          />

          <Group gap="xs">
            <Button
              size="xs"
              variant="default"
              loading={save.isRunning}
              disabled={!canLink}
              onClick={() => {
                if (chosen === undefined) {
                  return;
                }
                commit([...current, { calendarEventId: chosen.calendarEventId, relation }], () => {
                  setEventId('');
                });
              }}
            >
              {t('editor.calendar.link')}
            </Button>
          </Group>
          {full ? (
            <Text size="xs" c="dimmed">
              {t('editor.calendar.full')}
            </Text>
          ) : null}
          {alreadyLinked ? (
            <Text size="xs" c="dimmed">
              {t('editor.calendar.alreadyLinked')}
            </Text>
          ) : null}
        </Stack>
      )}

      <Stack component="section" gap="xs" aria-labelledby="editor-calendar-topics">
        <Stack gap={2}>
          <Text id="editor-calendar-topics" size="sm" fw={700}>
            {t('editor.calendar.topicsTitle')}
          </Text>
          {disabled ? null : (
            <Text size="xs" c="dimmed">
              {t('editor.calendar.topicsIntro')}
            </Text>
          )}
        </Stack>

        {topics === undefined ? null : topics.length === 0 ? (
          <Text size="sm" c="dimmed">
            {t('editor.calendar.noTopics')}
          </Text>
        ) : (
          <ul
            style={{ listStyle: 'none', margin: 0, padding: 0 }}
            aria-label={t('editor.calendar.linkedTopicsLabel')}
          >
            {topics.map((topic) => {
              const name = groupName(topic.groupCode, topic.name) ?? topic.name;
              return (
                <li key={topic.groupCode}>
                  <Group justify="space-between" gap="xs" wrap="nowrap" py={4}>
                    <Text size="sm">{name}</Text>
                    {disabled ? null : (
                      <Button
                        size="compact-xs"
                        variant="subtle"
                        disabled={save.isRunning}
                        aria-label={t('editor.calendar.removeTopic', { name })}
                        onClick={() => {
                          commitTopics(currentTopics.filter((code) => code !== topic.groupCode));
                        }}
                      >
                        {t('editor.calendar.remove')}
                      </Button>
                    )}
                  </Group>
                </li>
              );
            })}
          </ul>
        )}

        {disabled ? null : groupsRead.status === 'error' && groups === undefined ? (
          <Stack gap={6} align="flex-start">
            <Text size="sm">
              {t('editor.calendar.topicsFailed', { reason: describeProblem(groupsRead.error) })}
            </Text>
            <Button size="compact-xs" variant="default" onClick={groupsRead.refetch}>
              {t('common.tryAgain')}
            </Button>
          </Stack>
        ) : (
          <>
            <NativeSelect
              label={t('editor.calendar.topic')}
              disabled={groups === undefined || groups.length === 0}
              value={pickedTopic === undefined ? '' : pickedTopic.code}
              data={[
                {
                  value: '',
                  label:
                    groups === undefined
                      ? t('editor.calendar.readingTopics')
                      : t('editor.calendar.chooseTopic'),
                  disabled: true,
                },
                ...topicOptions(groups ?? [], t),
              ]}
              onChange={(event) => {
                setTopicCode(event.currentTarget.value);
              }}
            />
            <Group gap="xs">
              <Button
                size="xs"
                variant="default"
                loading={save.isRunning}
                disabled={!canAddTopic}
                onClick={() => {
                  if (pickedTopic === undefined) {
                    return;
                  }
                  commitTopics([...currentTopics, pickedTopic.code], () => {
                    setTopicCode('');
                  });
                }}
              >
                {t('editor.calendar.addTopic')}
              </Button>
            </Group>
            {topicsFull ? (
              <Text size="xs" c="dimmed">
                {t('editor.calendar.topicsFull')}
              </Text>
            ) : null}
            {topicAlready ? (
              <Text size="xs" c="dimmed">
                {t('editor.calendar.topicAlready')}
              </Text>
            ) : null}
          </>
        )}
      </Stack>

      {save.error === undefined ? null : (
        <Alert
          variant="light"
          role="alert"
          title={t('common.changeNotSaved')}
          withCloseButton
          closeButtonLabel={t('common.dismiss')}
          onClose={save.clearError}
          styles={{
            root: {
              backgroundColor: 'oklch(var(--mx-danger) / 0.10)',
              border: '1px solid oklch(var(--mx-danger) / 0.45)',
            },
            title: { color: 'oklch(var(--mx-danger))' },
          }}
        >
          <Text size="sm">{calendarLinksRefusal(save.error, true, t, topicWrite)}</Text>
        </Alert>
      )}
    </Stack>
  );
}
